import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { forumAPI, forumProfileAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowLeft, Heart, Loader2, Reply, MessagesSquare, Eye, CornerDownRight, ImagePlus, X, ChevronLeft, ChevronRight } from "lucide-react";
import ForumSpotlightRail from "@/components/ForumSpotlightRail";
import ForumUserIcons from "@/components/ForumUserIcons";
import MemberBadge from "@/components/MemberBadge";
import ReportDialog from "@/components/ReportDialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function flattenReplies(list, depth = 0, out = []) {
  (list || []).forEach((r) => {
    out.push({ ...r, _depth: depth });
    if (r.nested_replies?.length) flattenReplies(r.nested_replies, depth + 1, out);
  });
  return out;
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ForumDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [parentId, setParentId] = useState(null);
  const [parentName, setParentName] = useState("");
  const [posting, setPosting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images: [], index: 0 }
  const [profiles, setProfiles] = useState({});

  const load = useCallback(() => {
    forumAPI.getPost(id)
      .then((res) => {
        setPost(res.data.post);
        setReplies(res.data.replies || []);
        const ids = new Set();
        if (res.data.post?.author_id) ids.add(res.data.post.author_id);
        flattenReplies(res.data.replies || []).forEach((r) => r.author_id && ids.add(r.author_id));
        if (ids.size) {
          forumProfileAPI.batch([...ids])
            .then((r) => setProfiles(r.data || {}))
            .catch(() => {});
        }
      })
      .catch(() => toast.error("Post not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => load(), [load]);

  const liked = post?.likes?.includes(user?.id);
  const toggleLike = async () => {
    try { await forumAPI.toggleLike(id); load(); } catch { toast.error("Could not like post"); }
  };

  const openLightbox = (images, index) => setLightbox({ images, index });
  const lbPrev = useCallback(() => setLightbox((l) => l && ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length })), []);
  const lbNext = useCallback(() => setLightbox((l) => l && ({ ...l, index: (l.index + 1) % l.images.length })), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") lbPrev();
      else if (e.key === "ArrowRight") lbNext();
      else if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, lbPrev, lbNext]);

  const startReply = (rid, name) => {
    setParentId(rid);
    setParentName(name);
    document.getElementById("reply-box")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const pickReplyImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - replyImages.length;
    files.slice(0, remaining).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setReplyImages((prev) => [...prev, ev.target.result].slice(0, 5));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeReplyImage = (i) => setReplyImages((prev) => prev.filter((_, idx) => idx !== i));

  const submitReply = async () => {
    if (!content.trim() && replyImages.length === 0) return;
    setPosting(true);
    try {
      await forumAPI.createReply(id, {
        content,
        images: replyImages,
        parent_reply_id: parentId || undefined,
      });
      setContent(""); setReplyImages([]); setParentId(null); setParentName("");
      toast.success("Reply posted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post reply");
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!post) return null;

  const flat = flattenReplies(replies);

  return (
    <div className="mx-auto max-w-6xl">
      <button onClick={() => navigate("/forum")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Forum
      </button>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
      {/* ORIGINAL POST */}
      <Card className="overflow-hidden border-border bg-card">
        <div className="p-6">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="capitalize">{post.category?.replace("_", " ")}</Badge>
            {post.equipment_tag && post.equipment_tag !== "any" && (
              <Badge variant="outline" className="text-xs">{post.equipment_tag}</Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Avatar className="h-9 w-9 ring-2 ring-primary/40">
              {post.author_image ? <img src={post.author_image} alt="" className="h-full w-full rounded-full object-cover" /> : <AvatarFallback className="bg-secondary text-xs">{(post.author_username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>}
            </Avatar>
            <div>
              <p className="text-sm font-semibold">{post.author_username}</p>
              <p className="text-xs text-muted-foreground">{timeAgo(post.created_at)}</p>
            </div>
            <MemberBadge memberCategory={post.author_member_category} isFounding={post.author_is_founding} testId="forum-detail-member-badge" />
            <ForumUserIcons profile={profiles[post.author_id]} />
            {user && user.id !== post.author_id && (
              <div className="ml-auto"><ReportDialog contentType="forum" contentId={post.id} /></div>
            )}
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold leading-tight">{post.title}</h1>
          <div className="my-4 border-t border-border" />
          <p className="whitespace-pre-wrap leading-relaxed text-foreground/90">{post.content}</p>
          {post.images?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {post.images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openLightbox(post.images, i)}
                  className="overflow-hidden rounded-lg border border-border"
                  data-testid={`forum-post-image-${i}`}
                >
                  <img src={img} alt="" className="h-64 w-64 object-cover transition-transform hover:scale-105" />
                </button>
              ))}
            </div>
          )}
          <div className="mt-5 flex items-center gap-3 border-t border-border/40 pt-3">
            <Button size="sm" variant={liked ? "default" : "outline"} onClick={toggleLike} data-testid="forum-like-button" className={liked ? "bg-primary text-primary-foreground" : ""}>
              <Heart className={`mr-1.5 h-4 w-4 ${liked ? "fill-current" : ""}`} /> {post.likes?.length || 0}
            </Button>
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><MessagesSquare className="h-4 w-4" /> {post.reply_count} replies</span>
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Eye className="h-4 w-4" /> {post.views} views</span>
          </div>
        </div>
      </Card>

      {/* REPLY COMPOSER — visually distinct panel */}
      <Card id="reply-box" className="border border-accent/30 bg-secondary/40 p-4">
        <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wider text-accent">
          <Reply className="h-4 w-4" /> Join the conversation
        </h2>
        {parentId && (
          <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
            <CornerDownRight className="h-3.5 w-3.5" /> Replying to <span className="font-semibold text-foreground">{parentName}</span>
            <button onClick={() => { setParentId(null); setParentName(""); }} className="ml-1 text-primary hover:underline">cancel</button>
          </p>
        )}
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share your take, setup or advice..." rows={3} className="bg-card" data-testid="forum-reply-input" />

        {replyImages.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {replyImages.map((img, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-md border border-border">
                <img src={img} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeReplyImage(i)}
                  className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-background/80 text-foreground hover:bg-primary hover:text-primary-foreground"
                  data-testid={`forum-reply-image-remove-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="forum-reply-image-picker">
            <ImagePlus className="h-4 w-4" />
            Add photo
            <input type="file" accept="image/*" multiple className="hidden" onChange={pickReplyImages} />
          </label>
          <Button onClick={submitReply} disabled={posting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="forum-reply-submit">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post reply"}
          </Button>
        </div>
      </Card>

      {/* REPLIES */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <MessagesSquare className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">{post.reply_count} {post.reply_count === 1 ? "Reply" : "Replies"}</h2>
        </div>

        {flat.length === 0 ? (
          <Card className="border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No replies yet — start the discussion above.
          </Card>
        ) : (
          <div className="space-y-3">
            {flat.map((r) => {
              const nested = r._depth > 0;
              return (
                <div key={r.id} style={{ marginLeft: Math.min(r._depth, 4) * 24 }} className="relative" data-testid={`forum-reply-${r.id}`}>
                  {nested && <span className="absolute -left-4 top-0 h-full w-px bg-border" />}
                  <Card className={`border-border p-4 ${nested ? "bg-secondary/30" : "bg-card"}`}>
                    <div className="flex items-center gap-2.5">
                      {nested && <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      <Avatar className={nested ? "h-7 w-7" : "h-8 w-8"}>
                        {r.author_image ? <img src={r.author_image} alt="" className="h-full w-full rounded-full object-cover" /> : <AvatarFallback className="bg-secondary text-[10px]">{(r.author_username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>}
                      </Avatar>
                      <span className="text-sm font-semibold">{r.author_username}</span>
                      <MemberBadge memberCategory={r.author_member_category} isFounding={r.author_is_founding} />
                      <span className="text-xs text-muted-foreground">· {timeAgo(r.created_at)}</span>
                    </div>
                    {/* Reply: identity icons sit BELOW the name/date */}
                    <ForumUserIcons profile={profiles[r.author_id]} className="mt-1.5" />
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{r.content}</p>
                    {r.images?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {r.images.map((img, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => openLightbox(r.images, i)}
                            className="overflow-hidden rounded-md border border-border"
                            data-testid={`forum-reply-image-${r.id}-${i}`}
                          >
                            <img src={img} alt="" className="h-40 w-40 object-cover transition-transform hover:scale-105" />
                          </button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => startReply(r.id, r.author_username)} className="mt-2 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-primary" data-testid={`forum-reply-to-${r.id}`}>
                      <Reply className="h-3 w-3" /> Reply
                    </button>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </div>

        <ForumSpotlightRail />
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl border-border bg-card p-2" data-testid="forum-image-lightbox">
          {lightbox && (
            <div
              className="relative"
              onTouchStart={(e) => { lightbox._tx = e.changedTouches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = e.changedTouches[0].clientX - (lightbox._tx || 0);
                if (dx > 50) lbPrev();
                else if (dx < -50) lbNext();
              }}
            >
              <img src={lightbox.images[lightbox.index]} alt="" className="max-h-[80vh] w-full rounded-md object-contain" />
              {lightbox.images.length > 1 && (
                <>
                  <button
                    onClick={lbPrev}
                    data-testid="forum-lightbox-prev"
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/70 p-2 text-foreground hover:bg-background"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={lbNext}
                    data-testid="forum-lightbox-next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/70 p-2 text-foreground hover:bg-background"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-background/70 px-2.5 py-0.5 text-xs text-foreground">
                    {lightbox.index + 1} / {lightbox.images.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
